import React, { useState } from "react";
import PictureAvatar from "@/components/atomics/picture-avatar";
import OwnerView from "@/components/atomics/owner-view";
import ShareUrl from "@/components/atomics/share-url";
import Star from "@/components/atomics/star";
import StarVote from "@/components/atomics/star-vote";
import MotionModal from "@/components/atomics/motion-modal";
import {
  CollectionStats,
  CollectionMediaLoadingState,
} from "@/types/collection";
import { getFileStorageUrlSync } from "@/utils/storage";
import { formatStarCount } from "@/utils/format";

const formatRoyaltyPercentage = (royaltyPercent: number): string => {
  const rounded = Math.round(royaltyPercent * 100) / 100;

  if (rounded === Math.floor(rounded)) {
    return rounded.toString();
  }

  return rounded.toFixed(2).replace(/\.?0+$/, "");
};

interface HeaderSectionProps {
  walletAddress: string;
  stats: CollectionStats;
  loading: CollectionMediaLoadingState;
  isOwnerView: boolean;
  handleViewToggle: () => void;
  royaltyPercent: number;
  collection: string;
  stars?: number;
  onStarVote?: (rating: number) => void;
}

const HeaderSection = ({
  walletAddress,
  stats,
  loading,
  isOwnerView,
  handleViewToggle,
  royaltyPercent,
  collection,
  stars = 0,
  onStarVote,
}: HeaderSectionProps) => {
  const [showStarVote, setShowStarVote] = useState(false);

  const handleStarVote = (rating: number) => {
    if (onStarVote) {
      onStarVote(rating);
    }
    setShowStarVote(false);
  };

  return (
    <>
      <header className="w-full relative">
        {walletAddress === stats.owner_address && (
          <OwnerView
            isOwnerView={isOwnerView}
            handleViewToggle={handleViewToggle}
          />
        )}

        <div className="relative w-full custom-aspect-430-180">
          {loading.background ? (
            <div className="relative w-full h-full bg-[#525252]">
              <img src="/assets/ipfs-background-uploading.png" />
              <div className="absolute top-[34px] w-full h-full flex flex-col line-height-25 items-center text-[24px]">
                <span>LOADING...</span>
                <span>Awaiting IPFS data.</span>
              </div>
            </div>
          ) : (
            <img
              src={getFileStorageUrlSync(stats.background)}
              className="object-contain"
            />
          )}
          <div className="w-full absolute left-[50%] translate-x-[-50%] translate-y-[-80px] flex flex-col items-center justify-center">
            <PictureAvatar
              className="w-[150px]"
              image={getFileStorageUrlSync(stats.image)}
              isLoading={loading.image}
            />
            <div className="text-[32px]">
              <span
                className={`${
                  stats.name.length > 14 ? "text-[18px]" : ""
                } max-w-[340px]`}
              >
                {stats.name}
              </span>
            </div>
            <div className="text-[24px] text-[#6D6D6D]">
              <span>ROYALTY: {formatRoyaltyPercentage(royaltyPercent)}%</span>
            </div>
          </div>
          <div
            className="absolute bottom-[-49px] left-[20px] z-[1] hover:cursor-pointer flex gap-[10px] items-center justify-center"
            onClick={() => setShowStarVote(true)}
          >
            <Star />
            <span className="text-[14px]">{formatStarCount(stars)}</span>
          </div>
          <ShareUrl
            className={`absolute bottom-[-49px] right-[28px] z-[1] hover:cursor-pointer`}
            buttonColor="#0099FE"
            path={`collection/${collection}`}
            walletAddress={walletAddress}
          />
        </div>
      </header>

      <MotionModal isOpen={showStarVote}>
        <StarVote
          onClick={handleStarVote}
          onClose={() => setShowStarVote(false)}
        />
      </MotionModal>
    </>
  );
};

export default HeaderSection;
